import InputField from 'components/form-fields/formik/InputField';
import SelectField from 'components/form-fields/formik/SelectField';
import { FieldArray, FormikState } from 'formik';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  CustomerGroup,
  CustomerGroupFilterType,
  CustomerGroupFilterTypeValues,
  DropdownOption,
} from 'subdomains/cpo/types/charging-policy';
import { Button, Divider, Header, Label, Table } from 'semantic';
import { request } from 'utils/api';
import SearchDropdown from 'components/form-fields/formik/SearchDropdown';
import { Account } from 'types/account';
import { User } from 'types/user';
import { BillingPlan } from 'types/billingplan';
import DropdownField from 'components/form-fields/formik/DropdownField';
import { buildFormPath } from 'subdomains/cpo/utils/formpath';
import {
  createArrayFormItemWithId,
  useEnsureArrayFormItemIds,
} from 'utils/arrayformid';
import { CustomerGroupPreview } from './CustomerGroupPreview';

type Props = {
  basePath?: string;
  values: FormikState<CustomerGroup>['values'];
};

export const CustomerGroupFormFields = (props: Props) => {
  const { t } = useTranslation();

  const filtersWithTempIDs = useEnsureArrayFormItemIds(props.values?.filters);

  return (
    <>
      <Header
        as="h4"
        content={t(
          'customerGroup.addGroupTitle',
          '1. Add group name and description'
        )}
      />
      <p>
        {t(
          'customerGroup.addGroupDescription',
          'Add a unique name and a description to easily identify this group later.'
        )}
      </p>
      <InputField
        name="label"
        required
        label={t('customerGroup.label', 'Name')}
      />
      <InputField
        name="description"
        required
        label={t('customerGroup.description', 'Description')}
      />

      <Header
        as="h4"
        content={t(
          'customerGroup.selectFiltersTitle',
          '2. Select the customers to group'
        )}
      />
      <p>
        {t(
          'customerGroup.selectFiltersDescription',
          'Filter the customers by using categories or add the list of token IDs.'
        )}
      </p>
      <FieldArray name="filters">
        {({ push, remove, replace }) => (
          <>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={4}>
                    {t('customerGroup.filterCategory', 'Category')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('customerGroup.filterSelectedValues', 'Selected values')}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2} />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtersWithTempIDs?.map((filter, index) => {
                  const usedTypes = filtersWithTempIDs
                    .map((filter, i) => (i !== index ? filter.type : undefined))
                    .filter(Boolean) as CustomerGroupFilterType[];
                  return (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <Table.Row>
                          <Table.Cell
                            colSpan={3}
                            textAlign="center"
                            style={{
                              position: 'relative',
                              borderTop: 'none',
                              padding: 0.2,
                            }}>
                            <Label
                              content={t('customerGroup.or', 'OR')}
                              size="medium"
                              style={{
                                position: 'absolute',
                                top: '-0.5rem',
                                left: '21%',
                                padding: '0 0.5em',
                              }}
                            />
                          </Table.Cell>
                        </Table.Row>
                      )}
                      <Table.Row>
                        <Table.Cell>
                          <SelectField
                            name={buildFormPath(
                              `filters[${index}].type`,
                              props.basePath
                            )}
                            options={getFilterTypeOptions(t, usedTypes)}
                            onChange={(e, { value }) => {
                              if (value !== filter.type) {
                                replace(index, {
                                  type: value,
                                  value: undefined,
                                });
                              }
                            }}
                            label={t('customerGroup.filterBy', 'Filter by')}
                            required
                          />
                        </Table.Cell>
                        <Table.Cell>
                          {filter?.type === 'ACCOUNT' && (
                            <SearchDropdown
                              clearable
                              label={t('customerGroup.accounts', 'Accounts')}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              objectMode={false}
                              multiple
                              onDataNeeded={(body: Object) => {
                                return request<{ data: Account[] }>({
                                  path: '/1/accounts/search',
                                  method: 'POST',
                                  body: body,
                                });
                              }}
                            />
                          )}
                          {filter?.type === 'USER' && (
                            <SearchDropdown
                              clearable
                              label={t('customerGroup.users', 'Users')}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              objectMode={false}
                              multiple
                              onDataNeeded={(body: Object) => {
                                return request<{ data: User[] }>({
                                  path: '/1/users/search',
                                  method: 'POST',
                                  body: body,
                                });
                              }}
                            />
                          )}
                          {filter?.type === 'CONSUMER_CARD_BILLING_PLAN' && (
                            <SearchDropdown
                              clearable
                              label={t(
                                'customerGroup.billingPlans',
                                'Card Billing Plans'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              objectMode={false}
                              multiple
                              onDataNeeded={(body: Object) => {
                                return request<{ data: BillingPlan[] }>({
                                  path: '/1/billing-plans/search',
                                  method: 'POST',
                                  body: body,
                                });
                              }}
                            />
                          )}
                          {filter?.type === 'INFRA_TOKEN_PROVIDER' && (
                            <DropdownField
                              label={t(
                                'customerGroup.infraTokenProviders',
                                'Infra Token Providers'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              fluid
                              multiple
                              search
                              allowAdditions
                              selection
                              options={[]}
                            />
                          )}
                          {filter?.type === 'UID' && (
                            <DropdownField
                              label={t('customerGroup.uids', 'Token IDs')}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              fluid
                              multiple
                              search
                              allowAdditions
                              selection
                              options={[]}
                            />
                          )}
                          {filter?.type === 'ISSUER_NAME' && (
                            <DropdownField
                              label={t(
                                'customerGroup.issuerNames',
                                'Issuer Names'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              fluid
                              multiple
                              search
                              allowAdditions
                              selection
                              options={[]}
                            />
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            icon="trash"
                            basic
                            size="mini"
                            onClick={() => remove(index)}
                          />
                        </Table.Cell>
                      </Table.Row>
                    </React.Fragment>
                  );
                })}
              </Table.Body>
            </Table>
            <Divider hidden />
            <Button
              basic
              onClick={() => push(createArrayFormItemWithId({}))}
              icon="plus"
              content={t('customerGroup.addFilter', 'Add filter')}
            />
          </>
        )}
      </FieldArray>

      <Header
        as="h4"
        content={t(
          'customerGroup.reviewCustomerGroupTitle',
          'Review customer group'
        )}
      />
      <p>
        {t(
          'customerGroup.reviewCustomerGroupDescription',
          'Preview how the group will be created'
        )}
      </p>
      <CustomerGroupPreview values={props.values} />
    </>
  );
};

const getFilterTypeOptions = (
  t: TFunction<'translation', undefined>,
  usedTypes: CustomerGroupFilterType[] = []
): DropdownOption<CustomerGroupFilterType>[] => {
  return CustomerGroupFilterTypeValues.filter(
    (value) => !usedTypes.includes(value)
  ).map((value) => ({
    key: value,
    value,
    text: t(`customerGroup.filterType${value.toLowerCase()}`, value),
  }));
};
